const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export class ApiException extends Error {
  constructor(public status: number, public body: ApiError) {
    super(body.message);
    this.name = 'ApiException';
  }
}

async function request<T>(
  path: string,
  opts: RequestInit & { token?: string | null } = {},
): Promise<T> {
  const { token, ...init } = opts;
  const headers = new Headers(init.headers);
  // Content-Type ставим ТОЛЬКО при наличии тела — иначе GET/DELETE провоцируют
  // лишний CORS-preflight (OPTIONS) в проде (PWA и api-recipt.hamduh.su — разные origin).
  if (init.body !== undefined) headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);

  // Инвариант: api.* ВСЕГДА бросает ApiException, не голый TypeError.
  // Сетевые сбои (offline, DNS, connection refused, CORS) нормализуем сюда.
  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, { ...init, headers });
  } catch {
    throw new ApiException(0, { code: 'NETWORK', message: 'Нет связи с сервером' });
  }

  if (res.status === 204) return undefined as T;

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    // Не смогли распарсить JSON: на 2xx это сбой контракта (не возвращаем фейковое тело
    // под видом T), на ошибочном ответе — синтетическое тело для единообразия.
    if (res.ok) {
      throw new ApiException(res.status, {
        code: 'PARSE_ERROR',
        message: 'Не удалось разобрать ответ сервера',
      });
    }
    body = { code: 'INTERNAL', message: 'Ошибка сервера' };
  }

  if (!res.ok) throw new ApiException(res.status, body as ApiError);
  return body as T;
}

export const api = {
  get: <T>(path: string, token?: string | null) => request<T>(path, { method: 'GET', token }),
  // opts.keepalive: для фоновой отправки, переживающей выгрузку страницы (аналитика на pagehide).
  // keepalive — нативное поле RequestInit, проходит сквозь request → fetch без спецобработки.
  post: <T>(path: string, body?: unknown, token?: string | null, opts?: { keepalive?: boolean }) =>
    request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      token,
      keepalive: opts?.keepalive,
    }),
  put: <T>(path: string, body?: unknown, token?: string | null) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined, token }),
  delete: <T>(path: string, token?: string | null) =>
    request<T>(path, { method: 'DELETE', token }),
};
